import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseDownstreamDatabasesFilteredByTableQuery,
  DatabaseDownstreamDatabasesFilteredByTableQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamDatabasesFilteredByTableQuery';
import {
  DatabaseDownstreamDatabasesQuery,
  DatabaseDownstreamDatabasesQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamDatabasesQuery';
import { DatabasesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatabasesDisplayGridRowDataFragment';
import { DatabaseSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  DatabaseDownstreamDatabasesFilteredByTableQueryGql,
} from '../../queries/DatabaseDownstreamDatabasesFilteredByTableQueryGql';
import { DatabaseDownstreamDatabasesQueryGql } from '../../queries/DatabaseDownstreamDatabasesQueryGql';
import { getRowDataWithMaxCountsForDatabase } from '../database/functions/DatabaseRowDataFunctions';
import { DatabasesDisplayGridRowData } from '../database/types/DatabasesDisplayGridRowData';
import { DatabasesDisplayGrid } from '../sharedDisplayGrid/DatabasesDisplayGrid';
import { DatabasesValidSortFields } from '../sorting/sortProviders/DatabasesGridSortProvider';

type QueryVariables = DatabaseDownstreamDatabasesQueryVariables;
type QueryResults = DatabaseDownstreamDatabasesQuery;
type QueryResultListNode = DatabasesDisplayGridRowDataFragment;
type GridRowData = DatabasesDisplayGridRowData;

type QueryVariablesWithFilter = DatabaseDownstreamDatabasesFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseDownstreamDatabasesFilteredByTableQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamDatabasesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamDatabasesConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.databases && resultData.databases[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  return resultData && resultData.tables && resultData.tables[0];
}

function getRowDataForDatabase(
  database: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResults,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(database, selectMaxCountsResult(resultData));
}

function getRowDataForDatabaseForFilteredQuery(
  database: DatabasesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): DatabasesDisplayGridRowData {
  return getRowDataWithMaxCountsForDatabase(database, selectMaxCountsResultForFilteredQuery(resultData));
}

export type DatabaseDownstreamDatabasesQueryGridProps =
  GridSortProps<DatabasesValidSortFields> & {
    readonly databaseId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function DatabaseDownstreamDatabasesQueryGrid(props: DatabaseDownstreamDatabasesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyDownstreamDatabasesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDatabasesLineageGridMessage();

  return props.filteredTableId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={DatabaseDownstreamDatabasesFilteredByTableQueryGql}
      partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as DatabaseSortOrder, publishedConnectionsLineageEnabled }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDatabaseForFilteredQuery}
    >
      {({ rowStore }) =>
        <DatabasesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatabaseDownstreamDatabasesQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as DatabaseSortOrder, publishedConnectionsLineageEnabled }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDatabase}
      >
        {({ rowStore }) =>
          <DatabasesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />}
      </PaginatedQueryWrapper>
    );
}
